/**
 * v0.4 章④ F-2 (design-and-code MEDIUM指摘): シフト関連画面で共有する小さな整形ヘルパー。
 * `ShiftBoardPage.tsx`/`ShiftCoordinationPage.tsx` が同一セッション内でそれぞれ独立に
 * 再実装していた `personKey`/`isoToLocalHm`、および `ShiftPeriodsPage.tsx` と重複していた
 * `DRAFT_STATUS_LABELS` をここに集約する (§8-55 対策)。
 */

export type PersonRef = { employee_id: string | null; external_person_id: string | null };

/** employee_id/external_person_id から一意のキーを作る (lib/conditions/resolve.ts の personKey と同じ idiom) */
export const personKey = (p: PersonRef): string => (p.employee_id ? `E:${p.employee_id}` : `X:${p.external_person_id}`);

/** ⚠️ v0.4 現時点では「画面を開くブラウザ = 対象店舗と同一タイムゾーン」の前提を置く
 *  (渡来武は単一テナント・単一TZ)。ノードのタイムゾーン解決をフロントに持ち込むのは範囲外。 */
export const isoToLocalHm = (iso: string): string => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** 提出・承認等のタイムスタンプ表示用 (「M/D HH:MM」)。日付をまたいで代理入力される
 *  ケース (過去月分をまとめて後日提出等) があるため、時刻のみの isoToLocalHm では区別できない。 */
export const isoToLocalDateTime = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** shift_period_nodes.draft_status (期間×店舗の進捗) のラベル。shift_assignments.status
 *  (割当自体の状態) とは別軸のため混同しないこと。 */
export const DRAFT_STATUS_LABELS: Record<string, string> = {
  not_started: '未着手', collecting: '希望収集中', in_progress: '作成中',
  submitted: '提出済み', coordinated: '調整済み', confirmed: '確定',
};

/** shift_assignments.origin のラベル (F-1/F-2 で ShiftMyPage.tsx/ShiftBoardPage.tsx にそれぞれ
 *  独立に同じ値が定義されていたものを F-6 で共通化、§8-55 対策)。 */
export const ORIGIN_LABELS: Record<string, string> = {
  standing_pattern: '通常パターン', manual: '個別調整', preference_based: '希望反映', auto_generated: '自動生成案',
};

/** shift_assignments.status (割当自体の状態) のラベル。DRAFT_STATUS_LABELS (期間×店舗の進捗) とは
 *  別軸のため混同しないこと。'cancelled' は一覧系エンドポイントが除外して返すため通常は使われない。 */
export const STATUS_LABELS: Record<string, string> = {
  draft: '下書き', store_confirmed: '店舗案', confirmed: '確定', cancelled: 'キャンセル',
};

/** 'YYYY-MM-DD' + 'HH:MM' (ブラウザのローカルタイムゾーン基準) から ISO 日時文字列を組み立てる
 *  (v0.4 F-2 の ShiftBoardPage.tsx で独立実装されていたものを F-5 で共通化、§8-55 対策)。
 *  ⚠️ v0.4 現時点では「画面を開くブラウザ = 対象店舗と同一タイムゾーン」の前提 (上記 isoToLocalHm と同じ)。 */
export const buildLocalIso = (dateStr: string, hhmm: string): string => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = hhmm.split(':').map(Number);
  return new Date(y!, (m ?? 1) - 1, d, hh, mm, 0, 0).toISOString();
};
